import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import Select from 'react-select';
import type { CSSObjectWithLabel } from 'react-select';
import { PatientJourneyDimensionType } from '@trinity/chat/enums/conference';

export type TagDetailsDashboardDimensionTypeProps = {
  label?: string;
  value: PatientJourneyDimensionType | null | undefined;
  /** If provided, renders a react-select dropdown. Otherwise shows a read-only value. */
  onChange?: (value: PatientJourneyDimensionType | null) => void;
};

type OptionType = { value: PatientJourneyDimensionType | null; label: string };

const OPTIONS: OptionType[] = [
  { value: PatientJourneyDimensionType.SingleStageText, label: 'Single-Stage Text' },
  { value: PatientJourneyDimensionType.DimensionWideText, label: 'Dimension-Wide Text' },
];

export const TagDetailsDashboardDimensionType = ({
  label = 'Dimension Type',
  value,
  onChange,
}: TagDetailsDashboardDimensionTypeProps) => {
  const [state, setState] = useState(value ?? null);

  const handleChange = useCallback(
    (next: PatientJourneyDimensionType | null) => {
      setState(next);
      onChange?.(next);
    },
    [onChange]
  );

  return (
    <Root>
      <Label>{label}</Label>
      <Value>
        {onChange ? (
          <Select<OptionType>
            value={OPTIONS.find((o) => o.value === state) ?? null}
            options={OPTIONS}
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => String(o.value)}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 8 }) as CSSObjectWithLabel }}
            onChange={(v) => handleChange(v ? v.value : null)}
          />
        ) : (
          <Text>{state ?? 'N/A'}</Text>
        )}
      </Value>
    </Root>
  );
};

TagDetailsDashboardDimensionType.displayName = 'TagDetails.DashboardDimensionType';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  color: var(--gray-d);
  font-size: 14px;
`;

const Value = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Text = styled.span`
  font-size: 14px;
  color: var(--black);
`;
